import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import type { User } from '@/types';

type UserRole = User['role'];

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
}

/**
 * Role-Based Access Control (RBAC) Component
 * Per Bölüm 5.2.4: Enforce role-based access to routes
 * 
 * Usage:
 * <RoleProtectedRoute allowedRoles={['ADMIN', 'RESTAURANT']}>
 *   <RestaurantPanel />
 * </RoleProtectedRoute>
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const { isAuthenticated, user } = useAuthStore();

  // Debug logging (development only)
  if (import.meta.env.DEV) {
    console.log('[RoleProtectedRoute]', {
      isAuthenticated,
      user: user ? { id: user.id, phone: user.phone, role: user.role } : null,
      allowedRoles,
      hasAccess: user ? allowedRoles.includes(user.role) : false,
    });
  }

  // Not authenticated -> redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // User does not have required role -> redirect to home with error
  if (!allowedRoles.includes(user.role)) {
    console.warn(`[RoleProtectedRoute] Access denied. User role: ${user.role}, Required: ${allowedRoles.join(', ')}`);
    return <Navigate to="/" replace />;
  }

  // User has required role -> render children
  return <>{children}</>;
};
